import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import _intersection from 'underscore/modules/intersection';
import * as Yup from 'yup';
import RolesForm from './RolesForm';
import CardHeader from 'react/shared/components/true_link/main/form/wizard/CardHeader';
import NextPageButton from 'react/shared/components/true_link/main/form/wizard/NextPageButton';
import schema from 'react/shared/utils/schemas';
import trackUserData from 'react/signups/card/common/MarketoTracker';
import WizardCard from 'react/signups/card/common/WizardCard';
import cardForDataShape from 'react/signups/card/shapes/CardForData';

export default function Roles({ cardForData, moduleComplete, setCardForData, setTrusteeData }) {
  const paidCaregiverQuestionIsRequired = (values) =>
    _intersection(values, ['disability', 'elder', 'mental-health-challenges', 'other']).length > 0;

  const parentGuardianQuestionIsRequired = (values) => values.includes('teen-or-child'); //check if teen-or-child is in the selected options

  const validationSchema = Yup.object().shape({
    firstName: schema.firstName.required(),
    lastName: schema.lastName.required(),
    cardFor: Yup.array().min(1, 'Please select at least one option'),
    cardForOther: Yup.string().when('cardFor', (values) =>
      values.includes('other')
        ? Yup.string().required('This field is required')
        : Yup.string().nullable(),
    ),

    cardForPaidCaregiver: Yup.string().when('cardFor', {
      is: (cardFor) => paidCaregiverQuestionIsRequired(cardFor),
      then: (schema) => schema.required('The paid caregiver question is required'),
      otherwise: (schema) => schema.nullable(),
    }),

    cardForTeenOrChild: Yup.string().when('cardFor', {
      is: (cardFor) => parentGuardianQuestionIsRequired(cardFor),
      then: (schema) =>
        schema
          .required('The parent/guardian question is required')
          .test(
            'not-no',
            'Please have the parent or guardian sign up for an account or contact us for help.',
            (value) => value !== 'no',
          ),
      otherwise: (schema) => schema.nullable(), // ✅ Prevents validation when checkbox is unselected
    }),
  });

  const handleContinue = (values) => {
    const cardForPaidCaregiver = paidCaregiverQuestionIsRequired(values.cardFor)
      ? values.cardForPaidCaregiver
      : null;

    const cardForTeenOrChild = parentGuardianQuestionIsRequired(values.cardFor)
      ? values.cardForTeenOrChild
      : null;

    const marketoFieldForPaidCaregiverAnswer = 'Card_for_caregiver__c';
    const marketoFieldForParentGuardianAnswer = 'Card_For_Teen_or_Child__c';

    trackUserData({
      Company: `${values.firstName} ${values.lastName}`,
      [marketoFieldForPaidCaregiverAnswer]: cardForPaidCaregiver,
      ...($tlf.flipperShowChildSignUpFlow && {
        [marketoFieldForParentGuardianAnswer]: cardForTeenOrChild,
      }),
    });

    // Update trusteeData if the user doesn't have the specified values in cardForData
    // This ensures the trusteeData is updated when the user returns to the card_for step
    const specialNeedsOptions = ['disability', 'elder', 'mental-health-challenges', 'other'];
    const specialNeedsIsChecked = values.cardFor.some((item) => specialNeedsOptions.includes(item));
    if (!specialNeedsIsChecked) {
      setTrusteeData({ trustee: 'no' });
    }

    setCardForData({
      firstName: values.firstName,
      lastName: values.lastName,
      cardFor: values.cardFor,
      cardForOther: values.cardForOther,
      cardForPaidCaregiver,
      cardForTeenOrChild: values.cardForTeenOrChild,
    });
  };

  return (
    <>
      <WizardCard>
        <CardHeader>Tell us a little more about them</CardHeader>
        <Formik
          initialValues={cardForData}
          onSubmit={handleContinue}
          validationSchema={validationSchema}
        >
          {({ handleChange, handleSubmit, setFieldTouched, setFieldValue, values }) => (
            <>
              <RolesForm
                onChange={handleChange}
                paidCaregiverQuestionIsRequired={paidCaregiverQuestionIsRequired}
                parentGuardianQuestionIsRequired={parentGuardianQuestionIsRequired}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                values={values}
              />
              <NextPageButton
                disabled={moduleComplete}
                onClick={() => {
                  handleSubmit();
                }}
              />
            </>
          )}
        </Formik>
      </WizardCard>
    </>
  );
}

Roles.propTypes = {
  cardForData: cardForDataShape.isRequired,
  moduleComplete: PropTypes.bool.isRequired,
  setCardForData: PropTypes.func.isRequired,
  setTrusteeData: PropTypes.func.isRequired,
};
